import { Radio, DesignLanguage, useIsDesignLanguage, useIsTheme, Theme, Option } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode, useContext, useEffect, useRef } from 'react'
import { ModuleContainer, ShippingOptionItem } from '..'
import { ShippingOption as ShippingOptionModel } from '@afterpaytouch/portal-api/types'
import { ShippingOptionStatus, ShippingOptionErrorType } from '../../model'
import { useShippingOption, useIsDisabledUpdatingCheckout, useAllowedShippingOptions, useSelectedShippingOptionAllowed } from '../../state'

import { useMerchantCallback } from '../../utils/merchant'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import styles from './style.module.css'
import { valueIsNotNullOrUndefined } from '../../utils/validation'
import { useNonInitialEffect } from '../../utils/hooks'
import {
  usePreselectedShippingOption,
  useSelectedAndSortedShippingOptions,
  useShippingOptionHandlers,
  useShippingOptionWarningMessage,
  useUpdateShippingOption,
} from './hooks'
import { ShippingOptionContext } from './context'
import { TooltipWrapperProps } from './constants'
import { ShippingOptionWarningMessage } from './ShippingOptionWarningMessage'
import { ShippingOptionHeader } from './ShippingOptionHeader'

export const TooltipWrapper: FunctionComponent<TooltipWrapperProps> = ({ children, sortedShippingOptionsList, index, tooltipRef }): JSX.Element => {
  return index === sortedShippingOptionsList.length - 1 ? (
    <div id='ref-item' ref={tooltipRef}>
      {children}
    </div>
  ) : (
    <>{children}</>
  )
}

export const ShippingOptionList: FunctionComponent = () => {
  const { shippingOptionsStatus, selectedShippingOption, error: shippingOptionError, validationError } = useShippingOption()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const callbacks = useMerchantCallback()
  const isDisabled = useIsDisabledUpdatingCheckout()
  const allowedShippingOptions = useAllowedShippingOptions()
  const isSelectedShippingOptionExist = useSelectedShippingOptionAllowed(allowedShippingOptions)
  const tooltipRef = useRef(null)

  const hasSelectedShippingOption = valueIsNotNullOrUndefined(selectedShippingOption)
  const isShippingOptionUnableToUpdate = shippingOptionError === ShippingOptionErrorType.ShippingOptionUnableToUpdate

  const { isCollapse } = useContext(ShippingOptionContext)
  const { onRadioGroupChange, handleRadioItemOnClick, handleRadioItemOnKeyDown } = useShippingOptionHandlers()
  const updateCheckoutWithSelectedShipping = useUpdateShippingOption()
  const { sortedShippingOptionsList, option } = useSelectedAndSortedShippingOptions()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isPegasusTheme = useIsTheme(Theme.Pegasus)
  const isKelpieTheme = useIsTheme(Theme.Kelpie)
  const isPegasusOrKelpieTheme = isPegasusTheme || isKelpieTheme
  const showCollapsedSelectedOption = isCollapse && !isShippingOptionUnableToUpdate && valueIsNotNullOrUndefined(option)

  useShippingOptionWarningMessage()
  usePreselectedShippingOption()

  const renderRadioOptions = (): ReactNode => {
    if (!isArcadeDesignLanguage && showCollapsedSelectedOption) {
      return renderRadioItem(
        option,
        <ShippingOptionItem {...option} disabled={isDisabled} showArrow isCollapse={!isCollapse} hasSelectedShippingOption={hasSelectedShippingOption} />
      )
    }

    return (
      <Radio.Group value={selectedShippingOption?.id} indent name='shippingoption' onChange={onRadioGroupChange} kind='Option'>
        {sortedShippingOptionsList.map((shippingOption, index) => {
          return [
            renderRadioItem(
              shippingOption,
              <TooltipWrapper sortedShippingOptionsList={sortedShippingOptionsList} index={index} tooltipRef={tooltipRef}>
                <ShippingOptionItem
                  {...shippingOption}
                  showArrow={!isArcadeDesignLanguage && shippingOption?.id === selectedShippingOption?.id}
                  isCollapse={!isCollapse}
                  disabled={isDisabled}
                  hasSelectedShippingOption={hasSelectedShippingOption}
                />
              </TooltipWrapper>
            ),
          ]
        })}
      </Radio.Group>
    )
  }

  const renderRadioItem = (option: ShippingOptionModel | undefined, label: ReactNode): ReactNode =>
    option !== undefined && (
      <Radio.Button
        reversed={isArcadeDesignLanguage}
        roundedBorder={isArcadeDesignLanguage}
        kind='Option'
        label={() => label}
        onClick={() => handleRadioItemOnClick(option.name)}
        onKeyDown={(e) => handleRadioItemOnKeyDown(e, option)}
        indent
        labelWidth='Full'
        labelPlacement={!isArcadeDesignLanguage ? 'Middle' : 'Top'}
        value={option.id}
        icon={!isArcadeDesignLanguage ? 'Select' : 'Default'}
        disabled={isDisabled}
        key={`shipping-option-${option.id}`}
        testNameSpace={`shipping-option-${option.id}`}
        {...(showCollapsedSelectedOption && { defaultChecked: option.id === selectedShippingOption?.id })}
      />
    )

  const renderSingleOption = (): ReactNode => {
    if (isPegasusOrKelpieTheme) {
      return <Option text={<ShippingOptionHeader selectedShippingOption={selectedShippingOption} />} />
    }
    return (
      <ModuleContainer className={styles.containerPadding} showBorder={false} testNameSpace='shipping-option-div-container'>
        <ShippingOptionItem {...allowedShippingOptions[0]} disabled={isDisabled} />
      </ModuleContainer>
    )
  }

  const renderSingleOptionInUnselectedState = (): ReactNode => {
    return (
      <Radio.Group
        value={selectedShippingOption?.id}
        indent
        name='shippingoption'
        kind='Option'
        onChange={onRadioGroupChange}
        testNameSpace='single-shipping-option-unselected'
      >
        {allowedShippingOptions.map(() => {
          return [renderRadioItem(allowedShippingOptions[0], <ShippingOptionItem {...allowedShippingOptions[0]} disabled={isDisabled} />)]
        })}
      </Radio.Group>
    )
  }

  const renderItems = (allowedShippingOptions: ShippingOptionModel[]): ReactNode => {
    if (allowedShippingOptions.length === 1) {
      // not auto select if only one option after switching between PCL and PBI orders
      // and previous selected shipping option is invalid.
      return !isSelectedShippingOptionExist ? renderSingleOptionInUnselectedState() : renderSingleOption()
    } else if (allowedShippingOptions.length > 1) {
      return renderRadioOptions()
    }
  }

  useNonInitialEffect(() => {
    // trigger an update if there has been a change in the selected shipping option i.e. onShippingOptionChange has updated option with tax
    if (valueIsNotNullOrUndefined(selectedShippingOption) && shippingOptionsStatus === ShippingOptionStatus.RequiresCheckoutUpdate) {
      updateCheckoutWithSelectedShipping()
    }
  }, [shippingOptionsStatus])

  useEffect(() => {
    if (validationError !== undefined) {
      callbacks.sendMessage(validationError)

      const { severity, message } = validationError
      console[severity](`${String(severity)}: ${String(message)}`)
      logEvent('Merchant validation error', { validationError })
    }
  }, [validationError])

  return (
    <>
      {renderItems(allowedShippingOptions)}
      {!isArcadeDesignLanguage && <ShippingOptionWarningMessage />}
    </>
  )
}
